import React, { useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCheck,
	faExclamationTriangle,
	faSync,
	faTimesCircle,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import CustomModal from "../basics/interface/CustomModal"

/**
 * Modal de confirmation/sauvegarde de la fiche dépannage (remplace SaveModal).
 * `post` = depInfo.postDepannage ({saving, saved, error, errorFields}).
 * - errorFields non vide → liste bloquante des champs manquants (Fermer seul).
 * - `avertissements` (GE-46) : champs différables vides — la sauvegarde reste
 *   permise, la liste s'affiche sous la question.
 * - sinon → « Sauvegarder la fiche ? » Non/Oui ; le bouton Oui passe en spinner
 *   puis ✓ ; l'erreur API s'affiche dans le modal (thunk postDepannage silent).
 * - `documents` (Q17 a, Jordan 15.10) : quittances / factures qui ne
 *   correspondront plus (montant, moyen ou payeur changé) — archivées par le
 *   serveur au save, à régénérer ; confirmées par ce même « Oui ».
 * - Enter : confirmer / fermer selon l'état (comportement legacy).
 */
const SaveDepFicheV2 = ({
	post,
	avertissements = [],
	documents = [],
	titre = "Sauvegarder la fiche ?",
	question = "Voulez-vous enregistrer les modifications de cette fiche ?",
	save,
	onDone,
	onCancel,
}) => {
	const idle =
		!post.saving &&
		!post.saved &&
		!post.error &&
		post.errorFields.length === 0
	const hasFieldErrors = post.errorFields.length > 0
	const busy = post.saving || post.saved

	useEffect(() => {
		const onKey = (e) => {
			if (e.key !== "Enter") return
			e.preventDefault()
			if (idle) save()
			else if (post.saved) onDone()
			else if (hasFieldErrors || post.error) onCancel()
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [idle, hasFieldErrors, post.saved, post.error, save, onDone, onCancel])

	if (hasFieldErrors) {
		return (
			<CustomModal
				title="Champs manquants"
				subtitle="Corrigez les champs suivants avant de sauvegarder."
				onClose={onCancel}
				buttons={[
					{
						title: "Fermer",
						styleName: "primary",
						onClick: onCancel,
					},
				]}
			>
				<ul style={{ margin: 0, paddingLeft: 18 }}>
					{post.errorFields.map((field, index) => (
						<li
							key={index}
							style={{
								color: COLORS.danger,
								fontSize: 13,
								marginBottom: 4,
							}}
						>
							{field}
						</li>
					))}
				</ul>
			</CustomModal>
		)
	}

	return (
		<CustomModal
			title={titre}
			onClose={busy ? () => {} : post.error ? onCancel : onCancel}
			showCloseButton={!busy}
			buttons={
				post.saved
					? [
							{
								title: "Fermer",
								styleName: "primary",
								onClick: onDone,
							},
						]
					: [
							{
								title: "Non",
								styleName: "secondary",
								onClick: onCancel,
								disabled: busy,
							},
							{
								title: post.saving ? (
									<FontAwesomeIcon icon={faSync} spin />
								) : (
									"Oui"
								),
								styleName: "primary",
								onClick: save,
								disabled: busy,
							},
						]
			}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 10,
					fontSize: 13,
					color: COLORS.textMuted,
					textAlign: "center",
				}}
			>
				{post.saved ? (
					<>
						<FontAwesomeIcon
							icon={faCheck}
							size="2x"
							style={{ color: COLORS.success }}
						/>
						<span>Sauvegarde terminée</span>
					</>
				) : post.error ? (
					<>
						<FontAwesomeIcon
							icon={faTimesCircle}
							size="2x"
							style={{ color: COLORS.danger }}
						/>
						<span>
							{post.error.message ||
								post.error.msg ||
								"La sauvegarde a échoué."}
						</span>
						<span>Ce rapport d'erreur a été sauvegardé.</span>
					</>
				) : (
					<>
						<span>{question}</span>
						{avertissements.length > 0 && (
							<span style={{ color: COLORS.warning }}>
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									style={{ marginRight: 6 }}
								/>
								Fiche incomplète, à compléter plus tard :{" "}
								{avertissements.join(", ")}
							</span>
						)}
						{documents.map((texte) => (
							<span key={texte} style={{ color: COLORS.danger }}>
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									style={{ marginRight: 6 }}
								/>
								{texte}
							</span>
						))}
					</>
				)}
			</div>
		</CustomModal>
	)
}

export default SaveDepFicheV2
